import { img } from "../images";
import type { Service, ServiceCategory } from "./types";

export const urologyCategory: ServiceCategory = {
  slug: "urology",
  name: "Urology",
  short: "Specialist care for urinary and kidney health.",
  tagline: "Specialist care for urinary and kidney health.",
  image: img.urology,
  doctorSlug: "dr-m-v-arun-vijaiyan",
  intro:
    "Urology at AV Clinic addresses conditions affecting the urinary tract and male urinary system, including kidney stones, urinary problems and prostate-related conditions, with a focus on accurate diagnosis and appropriate management.",
  description:
    "The urinary system — the kidneys, ureters, bladder and urethra — affects daily comfort and long-term health, yet urinary symptoms are often quietly ignored until they become severe. Urology at AV Clinic focuses on accurate diagnosis and appropriate, individualised management of these conditions. From the sharp pain of a kidney stone to persistent urinary symptoms or prostate-related concerns, Dr. M. V. Arun Vijaiyan takes a thorough, patient-centred approach: understanding the problem fully before deciding whether conservative care, medical management, or a minimally invasive procedure such as endourology or laser stone treatment is most appropriate. The aim is clear communication, timely care and a plan you understand — so you can move forward with confidence.",
  conditions: [
    "kidney-stones",
    "urinary-problems",
    "prostate-care",
    "male-urinary-health",
    "endourology",
    "urological-consultation",
  ],
  concerns: [
    "Kidney stones",
    "Pain or difficulty passing urine",
    "Frequent or burning urination",
    "Blood in urine",
    "Prostate-related conditions",
    "Recurrent urinary infections",
  ],
  management: [
    "Complete urological evaluation and investigation",
    "Medical and procedural management of kidney stones",
    "Endourology and laser stone procedures where appropriate",
    "Management of prostate and urinary conditions",
    "Lifestyle and preventive guidance",
  ],
  faqs: [
    {
      q: "When should I see a urologist?",
      a: "You should consider a urology consultation if you have persistent urinary symptoms, kidney stones, blood in the urine, or any concern related to the urinary system. Early evaluation helps guide timely care.",
    },
    {
      q: "Is blood in urine always serious?",
      a: "Blood in the urine should always be evaluated, as it can indicate a range of conditions that a urologist can assess and manage.",
    },
    {
      q: "Do all kidney stones need surgery?",
      a: "No. Management depends on factors such as stone size, location, symptoms and clinical assessment. Many stones can be managed conservatively.",
    },
  ],
  related: ["kidney-stones", "urinary-problems", "prostate-care", "urological-consultation"],
  diagnosisSteps: [
    { title: "Consultation", desc: "A detailed discussion of your urinary symptoms and concerns." },
    { title: "Medical History & Examination", desc: "Relevant history and a focused assessment." },
    { title: "Investigations When Required", desc: "Tests such as urine analysis or imaging are used selectively, based on your symptoms." },
    { title: "Individualised Plan", desc: "Your findings guide a clear management plan." },
  ],
  specialistApproach: [
    { icon: "ri-headphone-line", title: "Listen", desc: "Understand your urinary symptoms and concerns." },
    { icon: "ri-search-eye-line", title: "Assess", desc: "Review history and perform a focused assessment." },
    { icon: "ri-stethoscope-line", title: "Diagnose", desc: "Confirm the cause with appropriate investigations." },
    { icon: "ri-calendar-check-line", title: "Plan", desc: "Agree on the most suitable treatment and follow-up." },
  ],
  patientEducation: [
    { icon: "ri-list-check-2", title: "Before Your Visit", desc: "Note your symptoms, how long they have lasted and any triggers." },
    { icon: "ri-file-list-3-line", title: "Bring Along", desc: "Previous reports, prescriptions and relevant test results." },
    { icon: "ri-question-line", title: "Questions to Ask", desc: "Ask about causes, treatment options, recovery and prevention." },
    { icon: "ri-refresh-line", title: "Plan Follow-Up", desc: "Some conditions need monitoring, so agree on a follow-up plan." },
  ],
  myths: [
    { myth: "Every kidney stone requires surgery.", fact: "Management depends on factors such as stone size, location, symptoms and clinical assessment." },
    { myth: "Blood in urine is nothing to worry about.", fact: "Blood in the urine should always be evaluated by a urologist." },
  ],
  seo: {
    title: "Urologist in Krishnagiri | Kidney Stone & Urinary Care | AV Clinic",
    description:
      "Specialist urology in Krishnagiri — kidney stones, urinary problems and prostate care by Dr. M. V. Arun Vijaiyan at AV Clinic. Book a consultation.",
  },
};

export const urologyServices: Service[] = [
  {
    slug: "kidney-stones",
    name: "Kidney Stones",
    category: "Urology",
    categorySlug: "urology",
    blurb: "Specialist evaluation and care for kidney stones.",
    tagline: "Understanding Kidney Stones. Finding the Right Care.",
    intro:
      "Kidney stones can cause severe pain and complications. AV Clinic provides thorough evaluation and appropriate management, including endourology and laser stone procedures where suitable, along with advice to reduce recurrence.",
    image: img.urology,
    symptoms: [
      { name: "Side or back pain", desc: "Severe, sharp pain in the flank that can come in waves." },
      { name: "Pain radiating to groin", desc: "Pain that moves toward the lower abdomen and groin." },
      { name: "Blood in urine", desc: "Pink, red or brown urine." },
      { name: "Nausea", desc: "Feeling sick or vomiting with the pain." },
      { name: "Painful urination", desc: "Burning or discomfort when passing urine." },
      { name: "Frequent urination", desc: "Urging to urinate more often than usual." },
    ],
    causes: [
      "Low fluid intake and dehydration",
      "Dietary factors",
      "Family history of stones",
      "Certain medical conditions",
      "Obesity and sedentary habits",
    ],
    types: [
      { name: "Calcium stones", desc: "The most common type, often linked to diet and calcium handling." },
      { name: "Uric acid stones", desc: "Associated with high purine intake and dehydration." },
      { name: "Other stone types", desc: "Less common types related to infection or metabolic factors." },
    ],
    riskFactors: [
      "Insufficient water intake",
      "Family or personal history of stones",
      "Hot climate and dehydration",
      "High-salt or high-oxalate diet",
      "Certain metabolic conditions",
    ],
    whenToSee: [
      "Pain is severe or not settling",
      "Blood is present in urine",
      "Fever or chills with the pain",
      "Stones keep recurring",
      "Difficulty passing urine",
    ],
    diagnosisSteps: [
      { title: "Consultation & History", desc: "Symptoms and risk factors are reviewed." },
      { title: "Urine & Blood Checks", desc: "Simple tests help assess kidney function and infection." },
      { title: "Imaging", desc: "An ultrasound or scan locates and sizes the stone." },
      { title: "Individualised Plan", desc: "Management depends on stone size, location and symptoms." },
    ],
    treatment: {
      medical: [
        "Pain management and medical support for suitable stones",
        "Dietary and fluid guidance",
        "Management of any underlying metabolic factors",
      ],
      procedures: [
        "Endourology and laser stone procedures where appropriate",
      ],
      lifestyle: [
        "Drinking enough water daily",
        "A balanced, low-salt diet",
        "Avoiding excess oxalate-rich foods where advised",
      ],
      followUp:
        "Small stones may pass with care, while larger or symptomatic stones may need a procedure. Follow-up depends on your treatment and response, and includes advice to reduce recurrence.",
    },
    myths: [
      { myth: "Every kidney stone requires surgery.", fact: "Management depends on factors such as stone size, location, symptoms and clinical assessment. Many stones pass with conservative care." },
      { myth: "Drinking more water cannot help.", fact: "Adequate hydration is one of the most important steps to prevent new stones from forming." },
    ],
    prevention: [
      "Drink enough water through the day",
      "Limit salt in your diet",
      "Maintain a healthy weight",
      "Follow dietary advice specific to your stone type",
    ],
    warningSigns: [
      "Uncontrollable severe pain",
      "Fever or chills",
      "Inability to pass urine",
      "Nausea with vomiting",
    ],
    management: [
      "Accurate diagnosis and stone assessment",
      "Medical and dietary management",
      "Endourology and laser stone procedures where appropriate",
      "Preventive advice to reduce recurrence",
    ],
    faqs: [
      { q: "Can kidney stones pass on their own?", a: "Small stones may pass naturally with hydration and care, but larger or symptomatic stones often need specialist management. Evaluation determines the best path." },
      { q: "Is laser treatment painful?", a: "Procedures are performed under appropriate anaesthesia. Your urologist will explain the process and what to expect." },
      { q: "Will stones come back?", a: "There is a risk of recurrence. Following fluid and dietary advice reduces the chance of new stones." },
      { q: "How much water should I drink?", a: "Adequate hydration is key. Your urologist will advise an appropriate intake based on your situation." },
      { q: "Can kidney stones be silent?", a: "Yes, some stones cause no symptoms and are found incidentally. Evaluation determines whether any action is needed." },
      { q: "How can I book a consultation?", a: "You can book through the appointment page, call the clinic, or message us on WhatsApp." },
    ],
    related: ["endourology", "urological-consultation", "urinary-problems"],
    relatedBlogs: ["kidney-stones-symptoms-and-care", "when-to-see-a-urologist"],
    seo: {
      title: "Kidney Stone Treatment in Krishnagiri | Urologist | AV Clinic",
      description:
        "Kidney stone treatment in Krishnagiri — evaluation, conservative management and laser stone procedures by a urologist at AV Clinic. Book a consultation.",
    },
  },
  {
    slug: "urinary-problems",
    name: "Urinary Problems",
    category: "Urology",
    categorySlug: "urology",
    blurb: "Addressing painful, frequent or difficult urination.",
    tagline: "Get Relief From Urinary Symptoms.",
    intro:
      "Urinary symptoms can be distressing and disruptive to daily life. AV Clinic evaluates symptoms such as burning, frequency, urgency and difficulty urinating to identify their cause and plan appropriate care.",
    image: img.urologyCare,
    symptoms: [
      { name: "Burning urination", desc: "Pain or a burning sensation when passing urine." },
      { name: "Frequent urination", desc: "Needing to urinate more often than usual." },
      { name: "Urgency", desc: "A sudden, strong need to urinate." },
      { name: "Difficulty urinating", desc: "Straining, a weak stream or trouble starting." },
      { name: "Recurrent infections", desc: "Urinary infections that keep coming back." },
    ],
    causes: [
      "Urinary tract infections",
      "Bladder conditions",
      "Prostate-related changes in men",
      "Kidney stones",
      "Certain medications",
    ],
    riskFactors: [
      "Diabetes",
      "Prostate enlargement",
      "Recurrent infections",
      "Dehydration or excess fluid intake",
    ],
    whenToSee: [
      "Symptoms are persistent or worsening",
      "There is blood in the urine",
      "Passing urine is painful or difficult",
      "You have fever with urinary symptoms",
    ],
    diagnosisSteps: [
      { title: "Consultation", desc: "Your symptoms and history are reviewed." },
      { title: "Urine Analysis", desc: "A simple test checks for infection or blood." },
      { title: "Focused Assessment", desc: "Examination and, when needed, imaging." },
      { title: "Individualised Plan", desc: "Treatment is matched to the cause." },
    ],
    treatment: {
      medical: [
        "Treatment of infections and underlying causes",
        "Management of bladder and prostate conditions",
      ],
      procedures: [],
      lifestyle: [
        "Adequate, balanced hydration",
        "Reducing bladder irritants such as excess caffeine",
      ],
      followUp:
        "Depending on the cause, review may be needed to confirm resolution and prevent recurrence.",
    },
    myths: [
      { myth: "Frequent urination is always from drinking too much.", fact: "It can also relate to infection, bladder or prostate conditions, and should be evaluated if persistent." },
    ],
    prevention: [
      "Stay well hydrated",
      "Empty the bladder regularly",
      "Limit bladder irritants",
    ],
    warningSigns: [
      "Blood in urine",
      "Fever or back pain",
      "Inability to pass urine",
    ],
    management: [
      "Urological assessment",
      "Identification of the underlying cause",
      "Appropriate treatment and advice",
    ],
    faqs: [
      { q: "Is frequent urination always a problem?", a: "Not always, but persistent or worsening frequency should be evaluated to rule out underlying causes." },
      { q: "What causes burning urination?", a: "Burning is commonly due to a urinary infection, but can have other causes that a urologist can assess." },
      { q: "When should I seek urgent care?", a: "If you cannot pass urine, have high fever, or see blood, seek prompt evaluation." },
    ],
    related: ["urological-consultation", "kidney-stones", "prostate-care"],
    relatedBlogs: ["frequent-urination-common-causes", "when-to-see-a-urologist"],
    seo: {
      title: "Urinary Problem Treatment in Krishnagiri | Urologist | AV Clinic",
      description:
        "Urinary problem treatment in Krishnagiri — burning, frequent or difficult urination evaluated and managed by a urologist at AV Clinic. Book a consultation.",
    },
  },
  {
    slug: "prostate-care",
    name: "Prostate Care",
    category: "Urology",
    categorySlug: "urology",
    blurb: "Evaluation and care for prostate-related concerns.",
    tagline: "Understanding Prostate Health.",
    intro:
      "Prostate-related conditions commonly affect urinary health, especially with age. AV Clinic offers specialist evaluation and guidance for prostate concerns, helping men understand their options and manage symptoms.",
    image: img.urologyCare,
    symptoms: [
      { name: "Difficulty urinating", desc: "Trouble starting or stopping the flow of urine." },
      { name: "Weak urine stream", desc: "A reduced or intermittent flow." },
      { name: "Frequent night-time urination", desc: "Waking often to pass urine." },
      { name: "Incomplete emptying", desc: "Feeling the bladder is not fully empty." },
    ],
    causes: [
      "Enlargement of the prostate (benign)",
      "Prostate inflammation",
      "Prostate-related conditions that need evaluation",
    ],
    riskFactors: [
      "Advancing age",
      "Family history of prostate problems",
      "Lifestyle and metabolic factors",
    ],
    whenToSee: [
      "Urinary symptoms are persistent or worsening",
      "Blood in urine or semen",
      "Pain or discomfort in the pelvic area",
      "You have concerns about prostate health",
    ],
    diagnosisSteps: [
      { title: "Consultation & History", desc: "Urinary symptoms and risk factors are reviewed." },
      { title: "Examination", desc: "A focused clinical assessment." },
      { title: "Investigations When Needed", desc: "Urine tests, imaging or PSA as clinically appropriate." },
      { title: "Individualised Plan", desc: "Treatment and monitoring matched to your findings." },
    ],
    treatment: {
      medical: [
        "Medication to manage urinary symptoms where appropriate",
        "Monitoring of the condition",
      ],
      procedures: [],
      lifestyle: [
        "Reducing caffeine and alcohol",
        "Maintaining a healthy weight",
        "Not delaying urination",
      ],
      followUp:
        "Prostate conditions often need ongoing monitoring. Follow-up is individualised to your findings and response.",
    },
    myths: [
      { myth: "Prostate problems always mean cancer.", fact: "No. Most prostate problems are benign. Proper evaluation determines the cause and appropriate care." },
      { myth: "Nothing can be done for prostate symptoms.", fact: "Many prostate-related urinary symptoms can be effectively managed or improved." },
    ],
    prevention: [
      "Maintain a healthy weight",
      "Exercise regularly",
      "Limit caffeine and alcohol at night",
    ],
    warningSigns: [
      "Blood in urine or semen",
      "Inability to pass urine",
      "Pelvic pain",
    ],
    management: [
      "Prostate-related assessment",
      "Appropriate investigations",
      "Individualised treatment and monitoring",
    ],
    faqs: [
      { q: "Do prostate problems always mean cancer?", a: "No. Most prostate problems are benign. Proper evaluation determines the cause and appropriate care." },
      { q: "How is an enlarged prostate diagnosed?", a: "Through a combination of symptom review, examination and tests such as urine analysis and imaging." },
      { q: "Can prostate symptoms be treated?", a: "Yes, many symptoms can be managed or improved with the right treatment and lifestyle changes." },
    ],
    related: ["urological-consultation", "urinary-problems", "male-urinary-health"],
    relatedBlogs: ["prostate-health-what-men-should-know"],
    seo: {
      title: "Prostate Care in Krishnagiri | Urologist | AV Clinic",
      description:
        "Prostate care in Krishnagiri — evaluation and management of prostate-related urinary symptoms by a urologist at AV Clinic. Book a consultation.",
    },
  },
  {
    slug: "male-urinary-health",
    name: "Male Urinary Health",
    category: "Urology",
    categorySlug: "urology",
    blurb: "Focused care for urinary health in men.",
    tagline: "Focused Care for Men's Urinary Health.",
    intro:
      "Male urinary health covers conditions of the urinary system that commonly affect men, including infections, stones and flow problems. AV Clinic provides focused evaluation and care.",
    image: img.urologyCare,
    symptoms: [
      { name: "Urinary infections", desc: "Burning, frequency and discomfort." },
      { name: "Urinary flow problems", desc: "Weak stream or difficulty starting." },
      { name: "Recurrent symptoms", desc: "Symptoms that keep returning." },
    ],
    causes: [
      "Infections",
      "Prostate-related changes",
      "Stones",
      "Lifestyle factors",
    ],
    whenToSee: [
      "Any persistent urinary symptom",
      "Blood in the urine",
      "Recurrent infections",
    ],
    diagnosisSteps: [
      { title: "Focused Assessment", desc: "Urinary symptoms are reviewed in detail." },
      { title: "Urine Analysis", desc: "Checks for infection or blood." },
      { title: "Further Tests if Needed", desc: "Imaging or other tests as clinically required." },
      { title: "Clear Plan", desc: "Treatment matched to the cause." },
    ],
    treatment: {
      medical: [
        "Treatment of infections and causes",
        "Management of flow problems",
      ],
      procedures: [],
      lifestyle: [
        "Adequate hydration",
        "Healthy bladder habits",
      ],
      followUp:
        "Depending on the cause, follow-up may include monitoring to prevent recurrence.",
    },
    management: [
      "Specialist urinary evaluation",
      "Treatment of the underlying condition",
      "Preventive health advice",
    ],
    faqs: [
      { q: "When should men have a urinary check?", a: "Any persistent urinary symptom — pain, blood, frequency or difficulty — warrants evaluation by a urologist." },
      { q: "Are male urinary infections common?", a: "They can occur and may relate to prostate or other factors. Evaluation determines the cause." },
    ],
    related: ["urological-consultation", "prostate-care", "urinary-problems"],
    relatedBlogs: ["when-to-see-a-urologist"],
    seo: {
      title: "Male Urinary Health in Krishnagiri | Urologist | AV Clinic",
      description:
        "Male urinary health in Krishnagiri — focused evaluation and care for infections, stones and flow problems by a urologist at AV Clinic.",
    },
  },
  {
    slug: "endourology",
    name: "Endourology",
    category: "Urology",
    categorySlug: "urology",
    blurb: "Minimally invasive approaches to urological procedures.",
    tagline: "Minimally Invasive Urological Care.",
    intro:
      "Endourology refers to minimally invasive techniques used within the urinary tract, such as laser stone procedures. These approaches can offer faster recovery for suitable cases, and are recommended only when clinically appropriate.",
    image: img.urologyCare,
    symptoms: [
      { name: "Kidney stones needing intervention", desc: "Stones that are large, symptomatic or not passing." },
      { name: "Stones not responding to care", desc: "Stones causing persistent symptoms." },
    ],
    whenToSee: [
      "Your urologist has identified a stone needing intervention",
      "Stones are causing recurrent symptoms",
    ],
    diagnosisSteps: [
      { title: "Suitability Assessment", desc: "Whether minimally invasive care is appropriate is determined." },
      { title: "Imaging & Planning", desc: "Stone location and size guide the approach." },
      { title: "Discussion", desc: "Procedure options and what to expect are explained." },
      { title: "Aftercare Plan", desc: "Recovery and follow-up are agreed." },
    ],
    treatment: {
      medical: [],
      procedures: [
        "Laser stone treatment and endourological techniques where suitable",
      ],
      lifestyle: [
        "Adequate hydration",
        "Follow-up dietary guidance",
      ],
      followUp:
        "Post-procedure review is planned, along with advice to reduce the chance of new stones.",
    },
    myths: [
      { myth: "All stones are best treated with surgery.", fact: "Many stones pass with conservative care. Intervention is reserved for stones that need it." },
    ],
    management: [
      "Assessment of suitability for minimally invasive care",
      "Discussion of procedural options",
      "Post-procedure care and follow-up",
    ],
    faqs: [
      { q: "Is laser stone treatment painful?", a: "Procedures are performed under appropriate anaesthesia. Your urologist will explain the process and what to expect." },
      { q: "How is recovery after a stone procedure?", a: "Recovery varies by procedure. Your urologist will give specific aftercare instructions." },
    ],
    related: ["kidney-stones", "urological-consultation"],
    relatedBlogs: ["kidney-stones-symptoms-and-care"],
    seo: {
      title: "Endourology & Laser Stone Treatment in Krishnagiri | AV Clinic",
      description:
        "Endourology and laser stone treatment in Krishnagiri — minimally invasive urological care by Dr. M. V. Arun Vijaiyan at AV Clinic.",
    },
  },
  {
    slug: "urological-consultation",
    name: "Urological Consultation",
    category: "Urology",
    categorySlug: "urology",
    blurb: "A thorough, confidential consultation for urinary concerns.",
    tagline: "A Clear Path to Better Urinary Health.",
    intro:
      "A urological consultation provides a complete evaluation of your urinary and kidney health, with clear explanation of findings and next steps — even when the cause of your symptoms is not yet clear.",
    image: img.urologyCare,
    symptoms: [
      { name: "Any urinary symptom", desc: "Pain, burning, frequency or difficulty." },
      { name: "Kidney stone concerns", desc: "Confirmed or suspected stones." },
      { name: "Prostate concerns", desc: "Symptoms or questions about prostate health." },
    ],
    whenToSee: [
      "You have a new or persistent urinary symptom",
      "You want a specialist opinion",
      "You need clear guidance on next steps",
    ],
    diagnosisSteps: [
      { title: "Complete History", desc: "Your symptoms and health are discussed openly." },
      { title: "Focused Assessment", desc: "Examination and selective investigations." },
      { title: "Clear Explanation", desc: "Findings are explained in plain language." },
      { title: "Next Steps", desc: "A practical plan and follow-up are agreed." },
    ],
    treatment: {
      medical: [],
      procedures: [],
      lifestyle: [
        "Appropriate hydration and bladder habits",
        "Guidance as recommended",
      ],
      followUp:
        "Follow-up is based on your findings — some concerns need monitoring, others a clear treatment plan.",
    },
    management: [
      "Complete history and assessment",
      "Appropriate investigations",
      "Clear explanation and care plan",
    ],
    faqs: [
      { q: "What should I bring to a urology consultation?", a: "Bring any previous reports, prescriptions and a list of your symptoms. Be ready to discuss your health history openly." },
      { q: "Is the consultation confidential?", a: "Yes. Consultations are private, professional and confidential." },
    ],
    related: ["urinary-problems", "kidney-stones", "prostate-care"],
    relatedBlogs: ["when-to-see-a-urologist"],
    seo: {
      title: "Urological Consultation in Krishnagiri | AV Clinic",
      description:
        "Urological consultation in Krishnagiri — a thorough, confidential evaluation of urinary and kidney health by a urologist at AV Clinic. Book now.",
    },
  },
  {
    slug: "laser-kidney-stone-treatment",
    name: "Laser Kidney Stone Treatment",
    category: "Urology",
    categorySlug: "urology",
    blurb: "Minimally invasive laser fragmentation of kidney stones.",
    tagline: "Precise, Minimally Invasive Stone Care.",
    intro:
      "Laser kidney stone treatment (laser lithotripsy) uses laser energy to fragment kidney stones into small pieces that can pass naturally. When stones are too large or symptomatic to pass on their own, this minimally invasive approach is an option — recommended only when clinically appropriate.",
    image: img.svcLaserKidneyStone,
    symptoms: [
      { name: "Large stones", desc: "Stones too large to pass on their own." },
      { name: "Persistent stone pain", desc: "Stones causing repeated flank or back pain." },
      { name: "Stones causing obstruction", desc: "Stones blocking urine flow." },
      { name: "Recurrent stones", desc: "Stones that keep returning." },
    ],
    causes: [
      "Stones that do not pass spontaneously",
      "Larger stones requiring intervention",
      "Stones causing complications or obstruction",
    ],
    whenToSee: [
      "Your urologist has identified a stone needing intervention",
      "Pain is severe or not settling",
      "Stones are causing repeated problems",
    ],
    diagnosisSteps: [
      { title: "Stone Assessment", desc: "Stone size, location and symptoms are reviewed." },
      { title: "Imaging & Planning", desc: "Imaging guides the best approach." },
      { title: "Discussion", desc: "Procedure and what to expect are explained." },
      { title: "Aftercare Plan", desc: "Recovery and follow-up are agreed." },
    ],
    treatment: {
      medical: [],
      procedures: [
        "Laser lithotripsy to fragment stones",
        "Minimally invasive endourological approach",
      ],
      lifestyle: [
        "Adequate hydration",
        "Dietary guidance",
      ],
      followUp:
        "Post-procedure review is planned, along with advice to reduce the chance of new stones forming.",
    },
    myths: [
      { myth: "Laser treatment is always needed for stones.", fact: "Many stones pass on their own. Laser treatment is reserved for stones that need intervention." },
      { myth: "Laser stone treatment is very painful.", fact: "The procedure is performed under appropriate anaesthesia, and most people recover well." },
    ],
    management: [
      "Assessment of whether laser treatment is appropriate",
      "Minimally invasive stone fragmentation",
      "Post-procedure care and follow-up",
    ],
    faqs: [
      { q: "Is laser kidney stone treatment painful?", a: "The procedure is performed under appropriate anaesthesia. Your urologist will explain the process and what to expect during recovery." },
      { q: "How long is recovery?", a: "Recovery varies by procedure and patient. Your urologist will give specific aftercare instructions." },
      { q: "Will stones come back after laser treatment?", a: "There is a chance of recurrence. Following fluid and dietary advice reduces the risk." },
    ],
    related: ["kidney-stones", "endourology", "advanced-endourology"],
    relatedBlogs: ["kidney-stones-symptoms-and-care"],
    seo: {
      title: "Laser Kidney Stone Treatment in Krishnagiri | Urologist | AV Clinic",
      description:
        "Laser kidney stone treatment (lithotripsy) in Krishnagiri — minimally invasive fragmentation of kidney stones by a urologist at AV Clinic. Book a consultation.",
    },
  },
  {
    slug: "ureteric-stone-treatment",
    name: "Ureteric Stone Treatment",
    category: "Urology",
    categorySlug: "urology",
    blurb: "Specialist care for stones lodged in the ureter.",
    tagline: "Targeted Care for Ureteric Stones.",
    intro:
      "Ureteric stones are stones that have moved into the ureter, the tube carrying urine from the kidney to the bladder. They can cause significant pain and obstruction. AV Clinic provides evaluation and appropriate management, including minimally invasive procedures when needed.",
    image: img.svcUretericStone,
    symptoms: [
      { name: "Severe flank pain", desc: "Sharp, colicky pain along the side and back." },
      { name: "Radiating pain", desc: "Pain that travels toward the groin." },
      { name: "Blood in urine", desc: "Pink, red or brown urine." },
      { name: "Obstructive symptoms", desc: "Nausea, vomiting and reduced urine flow." },
    ],
    causes: [
      "A kidney stone moving into the ureter",
      "Stones that become lodged",
      "Dehydration and dietary factors",
      "Recurrent stone formation",
    ],
    whenToSee: [
      "Pain is severe or colicky",
      "Blood is present in urine",
      "Fever or chills with pain",
      "Difficulty passing urine",
    ],
    diagnosisSteps: [
      { title: "Consultation & History", desc: "Symptoms and risk factors are reviewed." },
      { title: "Imaging", desc: "An ultrasound or scan locates and sizes the stone." },
      { title: "Assessment", desc: "Whether the stone can pass or needs intervention is decided." },
      { title: "Individualised Plan", desc: "The right management is chosen for you." },
    ],
    treatment: {
      medical: [
        "Pain and symptom management for suitable stones",
        "Medical expulsive care where appropriate",
      ],
      procedures: [
        "Minimally invasive removal of larger or obstructing stones",
      ],
      lifestyle: [
        "Adequate hydration",
        "Dietary guidance",
      ],
      followUp:
        "Small stones may pass with care, while obstructing stones may need a procedure. Follow-up depends on your situation.",
    },
    myths: [
      { myth: "All ureteric stones need surgery.", fact: "Small stones can pass on their own with care. Intervention is reserved for stones that obstruct or do not pass." },
      { myth: "Ureteric stones cause no lasting damage.", fact: "Obstructing stones can affect kidney function if left untreated, so timely evaluation is important." },
    ],
    warningSigns: [
      "Fever or chills with pain",
      "Inability to pass urine",
      "Uncontrollable pain",
    ],
    management: [
      "Accurate diagnosis and stone assessment",
      "Medical or minimally invasive management",
      "Preventive advice to reduce recurrence",
    ],
    faqs: [
      { q: "Can a ureteric stone pass on its own?", a: "Small stones can pass on their own with hydration and care. Larger or obstructing stones may need intervention." },
      { q: "What are the signs of obstruction?", a: "Severe colicky pain, nausea, fever or reduced urine flow can indicate obstruction and should be evaluated promptly." },
      { q: "How can I prevent recurrence?", a: "Adequate hydration and dietary guidance help reduce the chance of new stones forming." },
    ],
    related: ["kidney-stones", "laser-kidney-stone-treatment", "endourology"],
    relatedBlogs: ["kidney-stones-symptoms-and-care"],
    seo: {
      title: "Ureteric Stone Treatment in Krishnagiri | Urologist | AV Clinic",
      description:
        "Ureteric stone treatment in Krishnagiri — specialist evaluation and management of stones in the ureter by a urologist at AV Clinic. Book a consultation.",
    },
  },
  {
    slug: "benign-prostatic-hyperplasia",
    name: "Benign Prostatic Hyperplasia (BPH)",
    category: "Urology",
    categorySlug: "urology",
    blurb: "Understanding and managing an enlarged prostate.",
    tagline: "Managing an Enlarged Prostate, Comfortably.",
    intro:
      "Benign prostatic hyperplasia (BPH) is a non-cancerous enlargement of the prostate that commonly affects older men and can cause urinary symptoms. AV Clinic provides evaluation and management to relieve symptoms and improve quality of life.",
    image: img.svcBph,
    symptoms: [
      { name: "Weak urine stream", desc: "A slow or reduced flow of urine." },
      { name: "Difficulty starting", desc: "Trouble beginning to urinate." },
      { name: "Frequent night-time urination", desc: "Waking often to pass urine." },
      { name: "Urgency", desc: "A sudden, strong need to urinate." },
      { name: "Incomplete emptying", desc: "Feeling the bladder is not fully empty." },
    ],
    causes: [
      "Age-related prostate enlargement",
      "Hormonal changes",
      "Family history",
      "Lifestyle factors",
    ],
    riskFactors: [
      "Advancing age",
      "Family history of prostate problems",
      "Obesity",
      "Certain lifestyle habits",
    ],
    whenToSee: [
      "Urinary symptoms are persistent or worsening",
      "You wake frequently at night to urinate",
      "You have difficulty or pain passing urine",
      "You are concerned about prostate health",
    ],
    diagnosisSteps: [
      { title: "Consultation & History", desc: "Urinary symptoms and risk factors are reviewed." },
      { title: "Examination", desc: "A focused clinical assessment." },
      { title: "Investigations When Needed", desc: "Urine tests, imaging or PSA as clinically appropriate." },
      { title: "Individualised Plan", desc: "Treatment and monitoring matched to your findings." },
    ],
    treatment: {
      medical: [
        "Medication to relieve urinary symptoms where appropriate",
        "Monitoring of the condition",
      ],
      procedures: [
        "Minimally invasive options for suitable cases",
      ],
      lifestyle: [
        "Reducing caffeine and alcohol",
        "Maintaining a healthy weight",
        "Not delaying urination",
      ],
      followUp:
        "BPH often needs ongoing monitoring. Follow-up is individualised to your symptoms, findings and response to treatment.",
    },
    myths: [
      { myth: "BPH means you have prostate cancer.", fact: "BPH is a non-cancerous enlargement. It is not cancer, though a proper evaluation distinguishes the two." },
      { myth: "BPH cannot be treated.", fact: "BPH can be effectively managed with medication, lifestyle changes and, for suitable cases, minimally invasive procedures." },
    ],
    prevention: [
      "Maintain a healthy weight",
      "Exercise regularly",
      "Limit caffeine and alcohol at night",
    ],
    management: [
      "Evaluation of prostate-related symptoms",
      "Appropriate investigations",
      "Medication, lifestyle and procedural options",
    ],
    faqs: [
      { q: "Is BPH the same as prostate cancer?", a: "No. BPH is a non-cancerous enlargement of the prostate. It is not cancer, though evaluation helps distinguish the two." },
      { q: "Can BPH be treated without surgery?", a: "Yes. Many men manage BPH with lifestyle changes and medication. Minimally invasive procedures are an option for suitable cases." },
      { q: "Do prostate symptoms always need treatment?", a: "Not always. Mild symptoms can be monitored. Your urologist will advise based on your symptoms and findings." },
    ],
    related: ["prostate-care", "urinary-retention", "urological-consultation"],
    relatedBlogs: ["prostate-health-what-men-should-know"],
    seo: {
      title: "BPH (Enlarged Prostate) Treatment in Krishnagiri | Urologist | AV Clinic",
      description:
        "Benign prostatic hyperplasia (BPH) treatment in Krishnagiri — manage an enlarged prostate and urinary symptoms with a urologist at AV Clinic. Book a consultation.",
    },
  },
  {
    slug: "urinary-retention",
    name: "Urinary Retention",
    category: "Urology",
    categorySlug: "urology",
    blurb: "Evaluation and care for difficulty emptying the bladder.",
    tagline: "Relief From Urinary Retention.",
    intro:
      "Urinary retention is the inability to empty the bladder fully or at all. It can be sudden (acute) or develop gradually (chronic). AV Clinic provides prompt evaluation and management to relieve symptoms and address the underlying cause.",
    image: img.svcUrinaryRetention,
    symptoms: [
      { name: "Inability to urinate", desc: "A strong urge to urinate without being able to." },
      { name: "Distended bladder", desc: "Lower abdominal swelling and discomfort." },
      { name: "Weak or dribbling stream", desc: "Passing little urine despite straining." },
      { name: "Frequent small voids", desc: "Passing very small amounts often." },
    ],
    causes: [
      "Prostate enlargement (BPH)",
      "Bladder or nerve conditions",
      "Medication side effects",
      "Obstruction from stones or strictures",
      "Post-surgical retention",
    ],
    riskFactors: [
      "Prostate enlargement",
      "Certain medications",
      "Neurological conditions",
      "Prior pelvic or prostate surgery",
    ],
    whenToSee: [
      "You are unable to pass urine (urgent)",
      "You have a painful, distended lower abdomen",
      "Bladder emptying feels incomplete",
      "Urinary stream has become very weak",
    ],
    diagnosisSteps: [
      { title: "Prompt Assessment", desc: "Symptoms and history are reviewed urgently where needed." },
      { title: "Examination", desc: "The abdomen and bladder are examined." },
      { title: "Investigations", desc: "Tests and imaging identify the cause." },
      { title: "Plan", desc: "Relieving retention and treating the cause." },
    ],
    treatment: {
      medical: [
        "Relief of acute retention where needed",
        "Treatment of the underlying cause",
      ],
      procedures: [],
      lifestyle: [
        "Evacuating the bladder regularly",
        "Following medication guidance",
      ],
      followUp:
        "Follow-up depends on the cause. Treatment focuses on relieving retention and preventing recurrence.",
    },
    myths: [
      { myth: "Urinary retention always requires surgery.", fact: "Many cases are treated medically or by addressing the cause. Surgery is only needed for some." },
      { myth: "Holding urine is harmless.", fact: "Prolonged retention can cause discomfort and affect bladder and kidney health. It should be evaluated." },
    ],
    warningSigns: [
      "Unable to pass urine (urgent)",
      "Severe lower abdominal pain",
      "Fever with retention",
    ],
    management: [
      "Prompt relief of retention",
      "Identification and treatment of the cause",
      "Preventive advice",
    ],
    faqs: [
      { q: "Is urinary retention an emergency?", a: "Sudden inability to urinate with pain is a medical emergency and should be evaluated promptly." },
      { q: "What causes urinary retention?", a: "Common causes include prostate enlargement, medications, obstruction and nerve conditions. Your urologist can identify the cause." },
      { q: "Can urinary retention be treated?", a: "Yes. The cause is addressed and the bladder is relieved, with the aim of preventing recurrence." },
    ],
    related: ["prostate-care", "benign-prostatic-hyperplasia", "bladder-disorders"],
    relatedBlogs: ["prostate-health-what-men-should-know"],
    seo: {
      title: "Urinary Retention Treatment in Krishnagiri | Urologist | AV Clinic",
      description:
        "Urinary retention treatment in Krishnagiri — evaluation and care for difficulty emptying the bladder by a urologist at AV Clinic. Book a consultation.",
    },
  },
  {
    slug: "uti-management",
    name: "Urinary Tract Infection (UTI) Management",
    category: "Urology",
    categorySlug: "urology",
    blurb: "Effective diagnosis and care for recurring urinary infections.",
    tagline: "Clear, Recurrent UTIs, The Right Way.",
    intro:
      "Urinary tract infections cause burning, frequency and urgency and can recur. AV Clinic provides accurate diagnosis and appropriate treatment, with attention to men's urinary infections and recurrent cases that need a deeper look.",
    image: img.svcUti,
    symptoms: [
      { name: "Burning urination", desc: "Pain or burning when passing urine." },
      { name: "Frequent urination", desc: "Needing to urinate often." },
      { name: "Urgency", desc: "A sudden, strong urge to urinate." },
      { name: "Cloudy or strong-smelling urine", desc: "Changes in the look or smell of urine." },
      { name: "Pelvic discomfort", desc: "Lower abdominal pressure or pain." },
    ],
    causes: [
      "Bacterial infection of the urinary tract",
      "Incomplete bladder emptying",
      "Prostate enlargement",
      "Kidney stones or obstruction",
      "Hygiene and lifestyle factors",
    ],
    riskFactors: [
      "Recurrent infections",
      "Prostate enlargement",
      "Kidney stones",
      "Diabetes",
      "Dehydration",
    ],
    whenToSee: [
      "Symptoms are persistent or severe",
      "You have fever or back pain",
      "Infections keep recurring",
      "There is blood in the urine",
    ],
    diagnosisSteps: [
      { title: "Consultation", desc: "Symptoms and history are reviewed." },
      { title: "Urine Analysis", desc: "A simple test checks for infection or blood." },
      { title: "Culture When Needed", desc: "Identifies the specific bacteria for targeted care." },
      { title: "Individualised Plan", desc: "Treatment and prevention are tailored to you." },
    ],
    treatment: {
      medical: [
        "Antibiotic treatment suited to the infection",
        "Management of underlying causes",
      ],
      procedures: [],
      lifestyle: [
        "Adequate hydration",
        "Good hygiene",
        "Emptying the bladder regularly",
      ],
      followUp:
        "Recurrent UTIs may need further evaluation. Follow-up confirms resolution and addresses the cause.",
    },
    myths: [
      { myth: "UTIs only affect women.", fact: "UTIs can affect men too and may relate to prostate or kidney factors that warrant evaluation." },
      { myth: "UTIs resolve without treatment.", fact: "Many UTIs need appropriate treatment. Recurrent or severe infections should be evaluated, especially with fever." },
    ],
    prevention: [
      "Stay well hydrated",
      "Empty the bladder regularly",
      "Good hygiene",
    ],
    warningSigns: [
      "Fever or chills",
      "Back or flank pain",
      "Blood in urine",
    ],
    management: [
      "Accurate diagnosis",
      "Appropriate antibiotic treatment",
      "Addressing underlying causes",
      "Preventive guidance",
    ],
    faqs: [
      { q: "Can men get urinary tract infections?", a: "Yes. UTIs can affect men and may relate to prostate, kidney or other factors, so evaluation is important." },
      { q: "Why do my UTIs keep coming back?", a: "Recurrent UTIs may be linked to an underlying cause such as obstruction or stones. Your urologist can evaluate and address it." },
      { q: "What should I do if I have fever with a UTI?", a: "Fever with urinary symptoms can indicate a more serious infection. Seek prompt evaluation." },
    ],
    related: ["urinary-problems", "urological-consultation", "kidney-stones"],
    relatedBlogs: ["frequent-urination-common-causes"],
    seo: {
      title: "UTI Treatment in Krishnagiri | Urologist | AV Clinic",
      description:
        "Urinary tract infection (UTI) treatment in Krishnagiri — accurate diagnosis and care for recurrent infections by a urologist at AV Clinic. Book a consultation.",
    },
  },
  {
    slug: "bladder-disorders",
    name: "Bladder Disorders",
    category: "Urology",
    categorySlug: "urology",
    blurb: "Evaluation and care for overactive and underactive bladder.",
    tagline: "Understanding Bladder Health.",
    intro:
      "Bladder disorders include overactive bladder, underactive bladder and other conditions affecting bladder function and urine storage. AV Clinic provides evaluation and management to improve comfort and quality of life, including care for an overactive bladder.",
    image: img.svcBladderDisorders,
    symptoms: [
      { name: "Overactive bladder", desc: "A sudden, strong urge to urinate that is hard to control." },
      { name: "Frequent urination", desc: "Needing to urinate often, day and night." },
      { name: "Urgency", desc: "A pressing need to reach the toilet quickly." },
      { name: "Incomplete emptying", desc: "Feeling the bladder is not fully emptied." },
    ],
    causes: [
      "Overactive bladder muscle",
      "Nerve or muscle dysfunction",
      "Prostate-related changes",
      "Infections or irritation",
      "Lifestyle and dietary factors",
    ],
    riskFactors: [
      "Ageing",
      "Prostate enlargement",
      "Neurological conditions",
      "Recurrent infections",
    ],
    whenToSee: [
      "Urgency or frequency disrupts daily life",
      "You wake frequently at night",
      "You fear leakage or have accidents",
      "Symptoms are persistent or worsening",
    ],
    diagnosisSteps: [
      { title: "Consultation", desc: "Your bladder symptoms are reviewed in detail." },
      { title: "Symptom Diary", desc: "A diary of fluid intake and urination may be helpful." },
      { title: "Urine & Tests", desc: "Tests rule out infection and other causes." },
      { title: "Individualised Plan", desc: "Bladder retraining and treatment are planned." },
    ],
    treatment: {
      medical: [
        "Bladder retraining and habit techniques",
        "Medication where appropriate",
      ],
      procedures: [],
      lifestyle: [
        "Limiting caffeine and bladder irritants",
        "Regular urinary habits",
        "Adequate hydration",
      ],
      followUp:
        "Bladder conditions often improve with a structured plan. Follow-up monitors response and adjusts care.",
    },
    myths: [
      { myth: "Bladder urgency is just a normal part of ageing.", fact: "While more common with age, persistent urgency and frequency are treatable and should be evaluated." },
      { myth: "Nothing can be done for an overactive bladder.", fact: "Bladder retraining, lifestyle changes and medication can greatly improve symptoms." },
    ],
    management: [
      "Evaluation of bladder function",
      "Bladder retraining and lifestyle guidance",
      "Appropriate medication",
    ],
    faqs: [
      { q: "What is an overactive bladder?", a: "Overactive bladder is a condition causing a sudden, strong urge to urinate, often with frequency and urgency. It is very treatable." },
      { q: "Can bladder problems be improved?", a: "Yes. Bladder retraining, lifestyle changes and medication can significantly improve bladder symptoms." },
      { q: "When should I see a urologist for bladder symptoms?", a: "If urgency, frequency or urgency-related leakage disrupts your daily life or sleep, a urologist can help." },
    ],
    related: ["urinary-problems", "urinary-retention", "uti-management"],
    relatedBlogs: ["frequent-urination-common-causes"],
    seo: {
      title: "Bladder Disorder Treatment in Krishnagiri | Urologist | AV Clinic",
      description:
        "Bladder disorder treatment in Krishnagiri — overactive and underactive bladder evaluated and managed by a urologist at AV Clinic. Book a consultation.",
    },
  },
  {
    slug: "urological-cancer-care",
    name: "Urological Cancer Care",
    category: "Urology",
    categorySlug: "urology",
    blurb: "Compassionate evaluation and guidance for urological cancers.",
    tagline: "Supportive, Specialist Cancer Care.",
    intro:
      "Urological cancers affect organs such as the prostate, bladder, kidney and testicles. AV Clinic provides compassionate, confidential evaluation, appropriate investigations and clear guidance, and coordinates further specialist care when needed.",
    image: img.svcUroCancer,
    symptoms: [
      { name: "Blood in urine", desc: "Pink, red or brown urine." },
      { name: "Pelvic or back pain", desc: "Persistent discomfort that needs evaluation." },
      { name: "Urinary changes", desc: "New or worsening urinary symptoms." },
      { name: "Lump or swelling", desc: "A lump or swelling that warrants assessment." },
    ],
    causes: [
      "Age and family history",
      "Smoking",
      "Exposure to certain chemicals",
      "Chronic inflammation or infection",
      "Genetic factors",
    ],
    riskFactors: [
      "Advancing age",
      "Family history of urological cancers",
      "Smoking",
      "Occupational exposure",
      "Chronic urinary inflammation",
    ],
    whenToSee: [
      "You notice blood in the urine",
      "You have persistent symptoms that worry you",
      "You have a family history and want evaluation",
      "You need a specialist opinion",
    ],
    diagnosisSteps: [
      { title: "Consultation & History", desc: "Symptoms, history and risk factors are reviewed." },
      { title: "Investigations", desc: "Tests and imaging are used appropriately." },
      { title: "Discussion of Findings", desc: "Findings are explained clearly and sensitively." },
      { title: "Coordinated Plan", desc: "You are guided toward the appropriate care, including referral where needed." },
    ],
    treatment: {
      medical: [],
      procedures: [
        "Appropriate evaluation and referral",
        "Coordination of specialist care",
      ],
      lifestyle: [
        "Quitting smoking",
        "Healthy diet and weight",
        "Following advice provided",
      ],
      followUp:
        "Care is coordinated with the appropriate specialists. You will receive clear guidance on next steps and follow-up.",
    },
    myths: [
      { myth: "Blood in urine is always cancer.", fact: "Blood in urine can have many causes, but it always warrants evaluation to rule out significant conditions." },
      { myth: "There is nothing to do about urological cancer.", fact: "Early evaluation and timely specialist care greatly improve outcomes, and coordinated care is available." },
    ],
    warningSigns: [
      "Blood in urine",
      "Unexplained weight loss",
      "Persistent pain",
    ],
    management: [
      "Compassionate, confidential evaluation",
      "Appropriate investigations",
      "Clear guidance and coordinated referral",
    ],
    faqs: [
      { q: "What symptoms warrant evaluation for urological cancer?", a: "Blood in urine, persistent symptoms, unexplained pain or a lump should all be evaluated by a specialist." },
      { q: "Is blood in the urine always serious?", a: "Blood in urine has many possible causes, but it always warrants evaluation to rule out significant conditions." },
      { q: "How is care coordinated?", a: "AV Clinic provides evaluation and guides you toward the appropriate specialist care, with clear communication throughout." },
    ],
    related: ["urological-consultation", "prostate-care", "urinary-problems"],
    relatedBlogs: ["when-to-see-a-urologist"],
    seo: {
      title: "Urological Cancer Care in Krishnagiri | Urologist | AV Clinic",
      description:
        "Compassionate urological cancer care in Krishnagiri — evaluation, guidance and coordinated specialist care by a urologist at AV Clinic. Book a consultation.",
    },
  },
  {
    slug: "advanced-endourology",
    name: "Advanced Endourology",
    category: "Urology",
    categorySlug: "urology",
    blurb: "Advanced minimally invasive care for stone and urinary tract problems.",
    tagline: "Advanced Minimally Invasive Urology.",
    intro:
      "Advanced endourology uses modern minimally invasive techniques to manage stones and conditions of the urinary tract, offering precise, less-invasive treatment for suitable cases — recommended only when clinically appropriate.",
    image: img.svcAdvancedEndourology,
    symptoms: [
      { name: "Complex stones", desc: "Stones that are large, multiple or difficult to reach." },
      { name: "Stones not responding to care", desc: "Recurrent or stubborn stones." },
      { name: "Urinary tract obstruction", desc: "Blockage of urine flow." },
    ],
    causes: [
      "Kidney or ureteric stones",
      "Urinary tract obstruction",
      "Recurrent stone disease",
    ],
    whenToSee: [
      "Your urologist identifies a stone or problem needing intervention",
      "Stones are recurrent or complicated",
      "You want to explore minimally invasive options",
    ],
    diagnosisSteps: [
      { title: "Assessment", desc: "Whether advanced endourology is suitable is determined." },
      { title: "Imaging & Planning", desc: "Imaging guides the precise approach." },
      { title: "Discussion", desc: "Options and what to expect are explained." },
      { title: "Aftercare Plan", desc: "Recovery and follow-up are agreed." },
    ],
    treatment: {
      medical: [],
      procedures: [
        "Advanced minimally invasive endourological techniques",
      ],
      lifestyle: [
        "Adequate hydration",
        "Dietary guidance",
      ],
      followUp:
        "Post-procedure review and preventive advice are planned to keep stones from recurring.",
    },
    myths: [
      { myth: "Advanced techniques are always necessary.", fact: "They are reserved for cases where they are clinically appropriate and offer clear benefit." },
    ],
    management: [
      "Assessment of suitability",
      "Minimally invasive technique",
      "Post-procedure care and follow-up",
    ],
    faqs: [
      { q: "What is endourology?", a: "Endourology is a minimally invasive branch of urology that treats conditions of the urinary tract through small, precise techniques. It is used where clinically appropriate." },
      { q: "What are the benefits of minimally invasive care?", a: "For suitable cases, minimally invasive techniques can offer a shorter recovery and less discomfort compared with traditional surgery." },
      { q: "Is advanced endourology right for me?", a: "Your urologist will assess your condition and recommend the most appropriate approach, explaining the options clearly." },
    ],
    related: ["endourology", "laser-kidney-stone-treatment", "kidney-stones"],
    relatedBlogs: ["kidney-stones-symptoms-and-care"],
    seo: {
      title: "Advanced Endourology in Krishnagiri | Urologist | AV Clinic",
      description:
        "Advanced endourology in Krishnagiri — modern minimally invasive urological techniques by Dr. M. V. Arun Vijaiyan at AV Clinic. Book a consultation.",
    },
  },
  {
    slug: "laparoscopic-urology",
    name: "Laparoscopic Urology",
    category: "Urology",
    categorySlug: "urology",
    blurb: "Minimally invasive laparoscopic urological surgery.",
    tagline: "Minimally Invasive Surgical Care.",
    intro:
      "Laparoscopic urology uses keyhole techniques to perform urological surgery with small incisions, typically offering less discomfort and a shorter recovery for suitable procedures. It is recommended only when clinically appropriate.",
    image: img.svcLaparoscopic,
    symptoms: [
      { name: "Kidney or adrenal problems", desc: "Conditions needing surgical evaluation." },
      { name: "Stones or obstruction", desc: "Problems that may benefit from a surgical approach." },
      { name: "Recurrent urological conditions", desc: "Conditions requiring surgical management." },
    ],
    causes: [
      "Urological conditions requiring surgery",
      "Kidney, prostate or adrenal problems",
      "Stone or obstructive disease",
    ],
    whenToSee: [
      "Your urologist recommends a surgical approach",
      "You want to explore minimally invasive surgical options",
      "A condition requires surgical management",
    ],
    diagnosisSteps: [
      { title: "Suitability Assessment", desc: "Whether laparoscopic surgery is appropriate is determined." },
      { title: "Imaging & Planning", desc: "Pre-operative imaging guides the procedure." },
      { title: "Discussion", desc: "The procedure, benefits and recovery are explained." },
      { title: "Aftercare Plan", desc: "Recovery and follow-up are agreed." },
    ],
    treatment: {
      medical: [],
      procedures: [
        "Laparoscopic (keyhole) urological surgery where suitable",
      ],
      lifestyle: [
        "Following pre-operative and post-operative guidance",
      ],
      followUp:
        "Recovery and follow-up are planned. Your urologist will give specific instructions for after surgery.",
    },
    myths: [
      { myth: "Laparoscopic surgery is always the best option.", fact: "It is a valuable option for suitable cases. Your urologist will recommend the approach that is safest and most appropriate for you." },
    ],
    management: [
      "Assessment of suitability",
      "Minimally invasive keyhole surgery",
      "Post-operative care and follow-up",
    ],
    faqs: [
      { q: "What is laparoscopic urology?", a: "It is keyhole surgery used to perform urological procedures through small incisions, typically with less discomfort and a faster recovery for suitable cases." },
      { q: "Is laparoscopic surgery safe?", a: "When performed by an experienced urologist and suited to your condition, it is a safe and well-established approach." },
      { q: "How is recovery after laparoscopic surgery?", a: "Recovery is typically shorter than open surgery, but varies by procedure. Your urologist will advise specific aftercare." },
    ],
    related: ["advanced-endourology", "urological-consultation"],
    relatedBlogs: ["when-to-see-a-urologist"],
    seo: {
      title: "Laparoscopic Urology in Krishnagiri | Urologist | AV Clinic",
      description:
        "Laparoscopic urology in Krishnagiri — minimally invasive keyhole urological surgery by a urologist at AV Clinic. Book a consultation.",
    },
  },
];